import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  UserIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { appointmentsAPI } from '../../utils/api';
import { format, isToday, isTomorrow } from 'date-fns';
import { fr, enUS, es } from 'date-fns/locale';
import { parseLocalDate } from '../../utils/dateUtils';
import { toast } from 'react-toastify';
import { getErrorMessage } from '../../utils/errorTranslation';

const SharedCalendarWidget = () => {
  const { t, currentLanguage } = useLanguage();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('upcoming');
  const [showDropdown, setShowDropdown] = useState(false);

  const getLocale = () => {
    const lang = currentLanguage || 'fr';
    switch (lang) {
      case 'en': return enUS;
      case 'es': return es;
      default: return fr;
    }
  };

  const fetchSharedAppointments = useCallback(async () => {
    try {
      setLoading(true);
      const today = new Date();
      const start = new Date(today);
      start.setDate(today.getDate() - 7);
      const end = new Date(today);
      end.setDate(today.getDate() + 14);

      const params = {
        from_date: format(start, 'yyyy-MM-dd'),
        to_date: format(end, 'yyyy-MM-dd'),
        limit: 20
      };

      if (filter === 'confirmed') params.status = 'confirmed';
      if (filter === 'cancelled') params.status = 'cancelled';

      const response = await appointmentsAPI.getSharedAppointments(params);
      const data = response.data;
      setAppointments(data.appointments || []);
    } catch (error) {
      console.error('Error fetching shared appointments:', error);
      setAppointments([]);
      toast.error(getErrorMessage(error, t, t('dashboard.errors.calendarLoadFailed', 'Error loading calendar')));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchSharedAppointments();
  }, [fetchSharedAppointments]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed': return CheckCircleIcon;
      case 'pending': return ClockIcon;
      case 'cancelled': return XCircleIcon;
      default: return ClockIcon;
    }
  };

  const getDateLabel = (date) => {
    const appointmentDate = parseLocalDate(date);
    if (isToday(appointmentDate)) return t('appointments.calendar.today');
    if (isTomorrow(appointmentDate)) return t('common.tomorrow');
    return format(appointmentDate, 'd MMM', { locale: getLocale() });
  };

  const filters = [
    { value: 'upcoming', label: t('appointments.filters.upcoming') },
    { value: 'confirmed', label: t('appointments.filters.confirmed') },
    { value: 'cancelled', label: t('appointments.filters.cancelled') }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="card h-full rounded-lg border border-gray-200 shadow-sm"
    >
      <div className="card-header">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <CalendarIcon className="h-5 w-5 text-primary-600 mr-2" />
            {t('dashboard.widgets.sharedCalendar.title')}
          </h3>
          <div className="relative">
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="inline-flex items-center text-xs text-gray-500 hover:text-gray-700"
            >
              <FunnelIcon className="h-4 w-4 mr-1" />
              {t(`appointments.filters.${filter}`)}
            </button>
            {showDropdown && (
              <div className="absolute right-0 mt-2 w-36 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                {filters.map(option => (
                  <button
                    key={option.value}
                    onClick={() => {
                      setFilter(option.value);
                      setShowDropdown(false);
                    }}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50"
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="card-content">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <ClockIcon className="h-6 w-6 text-gray-400 animate-spin" />
          </div>
        ) : appointments.length === 0 ? (
          <div className="text-center text-sm text-gray-500 py-6">
            {t('dashboard.widgets.sharedCalendar.noAppointments')}
          </div>
        ) : (
          <div className="space-y-3">
            {appointments.map((appointment) => {
              const StatusIcon = getStatusIcon(appointment.status);
              return (
                <div key={appointment.id} className="border border-gray-100 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <StatusIcon className="h-4 w-4 text-gray-500 mr-2" />
                      <span className="text-sm font-medium text-gray-900">
                        {appointment.candidate_name || t('appointments.calendar.candidate', 'Candidat')}
                      </span>
                    </div>
                    <span className={`text-[10px] px-2 py-1 rounded-full ${getStatusColor(appointment.status)}`}>
                      {appointment.status}
                    </span>
                  </div>
                  <div className="mt-2 text-xs text-gray-600 space-y-1">
                    <div className="flex items-center">
                      <UserIcon className="h-4 w-4 mr-1" />
                      {appointment.recruiter_name}
                    </div>
                    <div>
                      {getDateLabel(appointment.date)} · {appointment.start_time}-{appointment.end_time}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default SharedCalendarWidget;
